import type {
  ChatCompletionResponse,
  RateLimitStatus,
  StructuredErrorResponse,
} from '@gofranz/gpt-muse-common';
import type {
  AuthenticatedChatCompletionRequest,
  ChatCompletionRequest,
  ChatMessageRequest,
} from '@gofranz/llm-core-common';
import type { ChatMessage, ChatMessageAttachment, ChatMessageImage, ReasoningSettings, WebSearchSettings } from '../lib/chat-db';
import { chatService } from '../services/chatService';
import { useRustyState } from '../state';
import { applySmartMemoryLimit } from '../utils/chatMemory';

interface StreamingOptions {
  messages: ChatMessage[];
  selectedModel: string;
  messageContent: string;
  attachment?: ChatMessageAttachment;
  isAuthenticated: boolean;
  selectedCharacter?: string | null;
  customPrompt?: string;
  isFirstMessage?: boolean;
  onRateLimitUpdate?: (info: RateLimitStatus) => void;
  onTokenUsage?: (usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number }) => void;
  onStreamingUpdate?: (content: string, reasoning: string, images: ChatMessageImage[]) => void;
  onError?: (error: string | StructuredErrorResponse) => void;
  abortSignal?: AbortSignal;
}

interface StreamingResult {
  message: ChatMessage | null;
  error?: string;
}

export async function streamChatCompletion(options: StreamingOptions): Promise<StreamingResult> {
  const {
    messages,
    selectedModel,
    messageContent,
    attachment,
    isAuthenticated,
    selectedCharacter,
    customPrompt,
    isFirstMessage = false,
    onRateLimitUpdate,
    onTokenUsage,
    onStreamingUpdate,
    onError,
    abortSignal,
  } = options;

  // Capture usage for persistence
  let capturedUsage: { prompt_tokens: number; completion_tokens: number; total_tokens: number } | undefined;

  // Load fresh settings from IndexedDB
  const freshReasoningSettings = await chatService.getModelPreferences(selectedModel);
  const freshWebSearchSettings = await chatService.getWebSearchPreferences(selectedModel);
  const freshChatMemoryLimit = await chatService.getChatMemoryLimit();

  // Apply chat memory limit to message history
  const limitedMessages = freshChatMemoryLimit === 0
    ? messages // 0 means unlimited
    : applySmartMemoryLimit(messages, freshChatMemoryLimit);

  // Build message history - simple text only (no attachments from history)
  const messageHistory: ChatMessageRequest[] = limitedMessages
    .filter(msg => msg.content.trim().length > 0)
    .map(msg => ({
      role: msg.role,
      content: { type: 'text' as const, content: msg.content },
    }));

  // Build current message with optional attachment
  const currentMessage: ChatMessageRequest = buildCurrentMessage(messageContent, attachment);
  messageHistory.push(currentMessage);

  // Add system prompt fields only for the first message
  const systemPromptFields = isFirstMessage && (selectedCharacter || customPrompt) ? {
    system_prompt_type: selectedCharacter === 'custom' ? 'custom' : (selectedCharacter || undefined),
    system_prompt_text: selectedCharacter === 'custom' ? customPrompt : undefined,
  } : {};

  try {
    const api = useRustyState.getState().api;
    let response: ChatCompletionResponse;

    if (isAuthenticated) {
      const requestData: AuthenticatedChatCompletionRequest = {
        model: selectedModel,
        messages: messageHistory,
        stream: true,
        temperature: 0.7,
        ...systemPromptFields,
        ...buildReasoningParams(freshReasoningSettings),
        ...buildPluginsArray(freshWebSearchSettings || undefined),
      };
      response = await api.chatCompletionsAuthenticated(requestData);
    } else {
      const requestData: ChatCompletionRequest = {
        model: selectedModel,
        messages: messageHistory,
        stream: true,
        temperature: 0.7,
        ...systemPromptFields,
        ...buildReasoningParams(freshReasoningSettings),
        ...buildPluginsArray(freshWebSearchSettings || undefined),
      };
      response = await api.chatCompletions(requestData);
    }

    // Extract rate limit headers for unauthenticated users
    if (!isAuthenticated && onRateLimitUpdate) {
      const info: RateLimitStatus = {
        limit_requests: parseInt(response.headers.get('x-ratelimit-limit-requests') || '0'),
        remaining_requests: parseInt(response.headers.get('x-ratelimit-remaining-requests') || '0'),
        reset_time: parseInt(response.headers.get('x-ratelimit-reset') || '0'),
        burst_limit_per_minute: parseInt(response.headers.get('x-ratelimit-burst-limit') || '0'),
        remaining_burst: parseInt(response.headers.get('x-ratelimit-remaining-burst') || '0'),
      };
      onRateLimitUpdate(info);
    }

    // Process the stream
    const result = await processStream(response.stream, {
      onStreamingUpdate,
      onTokenUsage: (usage) => {
        capturedUsage = usage; // Capture usage for persistence
        onTokenUsage?.(usage); // Still call parent callback for header display
      },
      onError,
      abortSignal,
    });

    if (result.content || result.images.length > 0) {
      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        conversationId: '', // Will be set by parent
        role: 'assistant',
        content: result.content || '',
        reasoning: result.reasoning || undefined,
        images: result.images.length > 0 ? result.images : undefined,
        usage: capturedUsage, // Add usage for persistence
        timestamp: new Date(),
      };

      return { message: assistantMessage };
    }

    return { message: null, error: 'No content received' };
  } catch (error) {
    // Don't show error for user-initiated cancellations
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('Request was cancelled by user');
      return { message: null };
    }

    let errorMessage: string | StructuredErrorResponse;
    if (error instanceof Error) {
      // Try to parse as structured error response
      try {
        errorMessage = JSON.parse(error.message);
      } catch {
        errorMessage = error.message;
      }
    } else {
      errorMessage = 'Sorry, I encountered an error while processing your message. Please try again.';
    }

    onError?.(errorMessage);

    // Create error message object for persistence
    const errorMessageObj: ChatMessage = {
      id: crypto.randomUUID(),
      conversationId: '', // Will be set by parent
      role: 'assistant',
      content: typeof errorMessage === 'string'
        ? `⚠️ Error: ${errorMessage}`
        : `⚠️ ${errorMessage.title}\n\n${errorMessage.message}`,
      isError: true,
      errorCode: typeof errorMessage === 'object' ? errorMessage.error_code : undefined,
      timestamp: new Date(),
    };

    return {
      message: errorMessageObj,
      error: typeof errorMessage === 'string' ? errorMessage : errorMessage.message
    };
  }
}

async function processStream(
  stream: ReadableStream<Uint8Array>,
  options: {
    onStreamingUpdate?: (content: string, reasoning: string, images: ChatMessageImage[]) => void;
    onTokenUsage?: (usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number }) => void;
    onError?: (error: string | StructuredErrorResponse) => void;
    abortSignal?: AbortSignal;
  }
): Promise<{ content: string; reasoning: string; images: ChatMessageImage[] }> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let accumulatedMessage = '';
  let accumulatedReasoning = '';
  const accumulatedImages: ChatMessageImage[] = [];
  let sseBuffer = '';

  // RAF throttling for smooth UI updates at 60fps
  let rafId: number | null = null;
  let hasUpdate = false;

  const scheduleUpdate = () => {
    if (!hasUpdate) {
      hasUpdate = true;

      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          options.onStreamingUpdate?.(accumulatedMessage, accumulatedReasoning, accumulatedImages);
          rafId = null;
          hasUpdate = false;
        });
      }
    }
  };

  while (true) {
    try {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      sseBuffer += chunk;

      const { contentEvents, reasoningEvents, imageEvents, remainingBuffer, error, usage } = parseSSEEvents(sseBuffer);
      sseBuffer = remainingBuffer;

      // Handle usage data
      if (usage) {
        options.onTokenUsage?.(usage);
      }

      // Handle errors
      if (error) {
        options.onError?.(error);
        // Cancel pending RAF before returning
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        return { content: '', reasoning: '', images: [] };
      }

      // Process events
      for (const content of contentEvents) {
        accumulatedMessage += content;
      }
      for (const reasoning of reasoningEvents) {
        accumulatedReasoning += reasoning;
      }
      for (const image of imageEvents) {
        // Deduplicate images by URL to prevent duplicates from split SSE chunks
        if (!accumulatedImages.some(existing => existing.url === image.url)) {
          accumulatedImages.push(image);
        }
      }

      // Schedule throttled update (max 60fps)
      if (contentEvents.length > 0 || reasoningEvents.length > 0 || imageEvents.length > 0) {
        scheduleUpdate();
      }

      if (chunk.includes('data: [DONE]')) {
        break;
      }
    } catch (readError) {
      console.error('Error reading stream chunk:', readError);
      // Cancel pending RAF before breaking
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      break;
    }
  }

  // Cancel any pending RAF and send final update
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
  }

  // Always send final update to ensure UI has latest content
  options.onStreamingUpdate?.(accumulatedMessage, accumulatedReasoning, accumulatedImages);

  return {
    content: accumulatedMessage,
    reasoning: accumulatedReasoning,
    images: accumulatedImages,
  };
}

function parseSSEEvents(buffer: string): {
  contentEvents: string[],
  reasoningEvents: string[],
  imageEvents: ChatMessageImage[],
  remainingBuffer: string,
  error?: string | StructuredErrorResponse,
  usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number }
} {
  const contentEvents: string[] = [];
  const reasoningEvents: string[] = [];
  const imageEvents: ChatMessageImage[] = [];
  let remainingBuffer = buffer;
  let usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number } | undefined;

  const parts = buffer.split('\n\n');
  const completeParts = buffer.endsWith('\n\n') ? parts.length : parts.length - 1;

  for (let i = 0; i < completeParts; i++) {
    const eventData = parts[i];
    const lines = eventData.split('\n');

    let fullData = '';
    const dataLineIndex = lines.findIndex(line => line.startsWith('data: '));
    if (dataLineIndex !== -1) {
      fullData = lines[dataLineIndex].slice(6);
      for (let j = dataLineIndex + 1; j < lines.length; j++) {
        fullData += '\n' + lines[j];
      }
    }

    if (fullData) {
      if (fullData.trim() === '[DONE]') {
        return { contentEvents, reasoningEvents, imageEvents, remainingBuffer: '', usage };
      }

      try {
        const parsed = JSON.parse(fullData);

        if (parsed.error) {
          return { contentEvents, reasoningEvents, imageEvents, remainingBuffer: '', error: parsed.error, usage };
        }

        if (parsed.choices?.[0]?.finish_reason === 'error') {
          const errorMessage = parsed.error?.message || 'An error occurred during streaming';
          return { contentEvents, reasoningEvents, imageEvents, remainingBuffer: '', error: errorMessage, usage };
        }

        if (parsed.usage) {
          usage = {
            prompt_tokens: parsed.usage.prompt_tokens || 0,
            completion_tokens: parsed.usage.completion_tokens || 0,
            total_tokens: parsed.usage.total_tokens || 0,
          };
        }

        if (parsed.choices && parsed.choices[0]?.delta) {
          const delta = parsed.choices[0].delta;

          if (delta.content) {
            contentEvents.push(delta.content);
          }

          if (delta.reasoning) {
            reasoningEvents.push(delta.reasoning);
          }

          if (delta.images && Array.isArray(delta.images)) {
            for (const image of delta.images) {
              if (image.image_url && image.image_url.url) {
                imageEvents.push({
                  url: image.image_url.url,
                  type: 'image_url' as const,
                  index: image.index,
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('Error parsing SSE JSON:', error);
      }
    }
  }

  remainingBuffer = buffer.endsWith('\n\n') ? '' : parts[parts.length - 1];
  return { contentEvents, reasoningEvents, imageEvents, remainingBuffer, usage };
}

function buildReasoningParams(settings: ReasoningSettings | null) {
  if (!settings) {
    return {};
  }

  const reasoning: any = {};

  if (settings.reasoningEnabled !== undefined) {
    reasoning.enabled = settings.reasoningEnabled;
  }

  if (settings.reasoningEnabled !== false) {
    if (settings.reasoningEffort) {
      reasoning.effort = settings.reasoningEffort;
    }

    if (settings.maxReasoningTokens) {
      reasoning.max_tokens = settings.maxReasoningTokens;
    }
  }

  if (settings.excludeReasoning !== undefined) {
    reasoning.exclude = settings.excludeReasoning;
  }

  return Object.keys(reasoning).length > 0 ? { reasoning } : {};
}

function buildPluginsArray(settings?: WebSearchSettings) {
  if (!settings || !settings.webSearchEnabled) {
    return {};
  }

  const webPlugin: any = {
    id: 'web',
  };

  if (settings.webSearchContextSize) {
    if (settings.webSearchContextSize <= 3000) {
      webPlugin.search_context_size = 'low';
    } else if (settings.webSearchContextSize <= 10000) {
      webPlugin.search_context_size = 'medium';
    } else {
      webPlugin.search_context_size = 'high';
    }
  }

  return {
    plugins: [webPlugin]
  };
}

/**
 * Build current message with optional attachment (image or audio)
 * If no attachment, returns simple text message
 * If attachment present, returns message with content array containing text and media
 */
function buildCurrentMessage(messageContent: string, attachment?: ChatMessageAttachment): ChatMessageRequest {
  if (!attachment) {
    // Simple text message
    return {
      role: 'user' as const,
      content: { type: 'text' as const, content: messageContent },
    };
  }

  // Build content array with text and attachment
  const contentParts: any[] = [
    { type: 'text', text: messageContent }
  ];

  if (attachment.type === 'image') {
    // Add image attachment
    contentParts.push({
      type: 'image_url',
      image_url: {
        url: `data:${attachment.mimeType};base64,${attachment.data}`
      }
    });
  } else if (attachment.type === 'audio') {
    // Add audio attachment
    // Extract format from mimeType (e.g., "audio/wav" -> "wav")
    const format = attachment.mimeType.split('/')[1] || 'wav';
    contentParts.push({
      type: 'input_audio',
      input_audio: {
        data: attachment.data,
        format: format
      }
    });
  }

  return {
    role: 'user' as const,
    content: { type: 'parts' as const, content: contentParts },
  };
}